import React, { createContext, useContext, useReducer, useCallback, ReactNode } from 'react';
import { ToastContainer, ToastData } from '../components/molecules/ToastContainer';

// Toast action types
type ToastAction =
  | { type: 'ADD_TOAST'; payload: ToastData }
  | { type: 'REMOVE_TOAST'; payload: string }
  | { type: 'CLEAR_ALL_TOASTS' };

// Toast state interface
interface ToastState {
  toasts: ToastData[];
}

// Toast context interface
interface ToastContextType {
  toasts: ToastData[];
  addToast: (toast: Omit<ToastData, 'id'>) => string;
  removeToast: (id: string) => void;
  clearAllToasts: () => void;
  // Convenience methods for common toast types
  showSuccess: (title: string, message?: string, options?: Partial<ToastData>) => string;
  showError: (title: string, message?: string, options?: Partial<ToastData>) => string;
  showWarning: (title: string, message?: string, options?: Partial<ToastData>) => string;
  showInfo: (title: string, message?: string, options?: Partial<ToastData>) => string;
}

// Create context
const ToastContext = createContext<ToastContextType | undefined>(undefined);

// Toast reducer
const toastReducer = (state: ToastState, action: ToastAction): ToastState => {
  switch (action.type) {
    case 'ADD_TOAST':
      return {
        ...state,
        toasts: [...state.toasts, action.payload]
      };
    case 'REMOVE_TOAST':
      return {
        ...state,
        toasts: state.toasts.filter(toast => toast.id !== action.payload)
      };
    case 'CLEAR_ALL_TOASTS':
      return {
        ...state,
        toasts: []
      };
    default:
      return state;
  }
};

// Generate unique ID for toasts
const generateToastId = (): string => {
  return `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Toast provider component
interface ToastProviderProps {
  children: ReactNode;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
  maxToasts?: number;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({
  children,
  position = 'top-right',
  maxToasts = 5
}) => {
  const [state, dispatch] = useReducer(toastReducer, { toasts: [] });

  const addToast = useCallback((toast: Omit<ToastData, 'id'>): string => {
    const id = generateToastId();
    const newToast: ToastData = {
      ...toast,
      id,
      duration: toast.duration ?? 5000
    };
    
    dispatch({ type: 'ADD_TOAST', payload: newToast });
    return id;
  }, []);

  const removeToast = useCallback((id: string) => {
    dispatch({ type: 'REMOVE_TOAST', payload: id });
  }, []);

  const clearAllToasts = useCallback(() => {
    dispatch({ type: 'CLEAR_ALL_TOASTS' });
  }, []);

  // Convenience methods
  const showSuccess = useCallback((
    title: string, 
    message?: string, 
    options: Partial<ToastData> = {}
  ): string => {
    return addToast({
      type: 'success',
      title,
      message,
      ...options
    });
  }, [addToast]);

  const showError = useCallback((
    title: string, 
    message?: string, 
    options: Partial<ToastData> = {}
  ): string => {
    return addToast({
      type: 'error',
      title,
      message,
      duration: 7000, // Longer duration for errors
      ...options
    });
  }, [addToast]);

  const showWarning = useCallback((
    title: string, 
    message?: string, 
    options: Partial<ToastData> = {}
  ): string => {
    return addToast({
      type: 'warning',
      title,
      message,
      duration: 6000,
      ...options
    });
  }, [addToast]);

  const showInfo = useCallback((
    title: string, 
    message?: string, 
    options: Partial<ToastData> = {}
  ): string => {
    return addToast({
      type: 'info',
      title,
      message,
      ...options
    });
  }, [addToast]);

  const contextValue: ToastContextType = {
    toasts: state.toasts,
    addToast,
    removeToast,
    clearAllToasts,
    showSuccess,
    showError,
    showWarning,
    showInfo
  };

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastContainer
        toasts={state.toasts}
        onRemoveToast={removeToast}
        position={position}
        maxToasts={maxToasts}
      />
    </ToastContext.Provider>
  );
};

// Custom hook to use toast context
export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  
  return context;
};

// Specialized hooks for cart notifications
export const useCartToast = () => {
  const toast = useToast();

  const showAddToCartSuccess = useCallback((itemName: string, quantity: number = 1) => {
    return toast.showSuccess(
      'Added to Cart!',
      `${quantity} ${quantity === 1 ? 'item' : 'items'} added to your cart`,
      {
        action: {
          label: 'View Cart',
          onClick: () => {
            // Navigate to cart page
            window.location.href = '/cart';
          }
        }
      }
    );
  }, [toast]);

  const showRemoveFromCartSuccess = useCallback((itemName: string) => {
    return toast.showSuccess(
      'Removed from Cart',
      `${itemName} has been removed from your cart`
    );
  }, [toast]);

  const showCartError = useCallback((message: string) => {
    return toast.showError(
      'Cart Error',
      message
    );
  }, [toast]);

  const showCartWarning = useCallback((message: string) => {
    return toast.showWarning(
      'Cart Warning',
      message
    );
  }, [toast]);

  const showQuantityUpdated = useCallback((itemName: string, quantity: number) => {
    return toast.showInfo(
      'Quantity Updated',
      `${itemName} quantity updated to ${quantity}`
    );
  }, [toast]);

  const showCartCleared = useCallback(() => {
    return toast.showInfo(
      'Cart Cleared',
      'All items have been removed from your cart'
    );
  }, [toast]);

  const showFreeShippingProgress = useCallback((remainingAmount: number) => {
    return toast.showInfo(
      'Free Shipping Progress',
      `Add €${remainingAmount.toFixed(2)} more for free shipping!`,
      {
        duration: 4000
      }
    );
  }, [toast]);

  return {
    showAddToCartSuccess,
    showRemoveFromCartSuccess,
    showCartError,
    showCartWarning,
    showQuantityUpdated,
    showCartCleared,
    showFreeShippingProgress
  };
};

export default ToastProvider;
